import {
  TableCellProps,
  WbPagination,
  WbTable,
  WbResponsiveTableToolbar,
  getObjectPropFromString,
  useDockableGroupContext,
} from '@agilelab/plugin-wb-platform';
import { Box, Typography, makeStyles, useTheme } from '@material-ui/core';
import {
  Dispatch,
  FC,
  KeyboardEvent,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { SystemItem } from './DataAccessTab';

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,

    padding: '12px',
  },
  table: {
    flex: '1 1 0',
    minHeight: 0,
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 auto',
    },
  },
}));

export const SystemList: FC<{
  selectedRow: SystemItem | undefined;
  setSelectedRow: Dispatch<SetStateAction<SystemItem | undefined>>;
  systems?: SystemItem[];
}> = ({ systems, selectedRow, setSelectedRow }) => {
  const theme = useTheme();
  const classes = useStyles();
  const dockableCtx = useDockableGroupContext();
  const [search, setSearch] = useState<string>('');
  const [tableRows, setTableRows] = useState<SystemItem[]>(systems ?? []);

  const [paginationOP, setPaginationOP] = useState<{
    limit: number;
    offset: number;
  }>({
    limit: 25,
    offset: 0,
  });

  const columns = useMemo<TableCellProps<SystemItem>[]>(
    () => [
      {
        field: 'displayName',
        fieldRender: item => (
          <Typography>{`${item.displayName} v${
            item.systemVersion.split('.')[0]
          }`}</Typography>
        ),
        tooltip: false,
        headerName: 'Name',
      },
      {
        field: 'domain',
        tooltip: false,
        typographyProps: {
          component: 'p',
        },
        headerName: 'Domain',
      },
      {
        field: 'kind',
        tooltip: false,
        typographyProps: {
          component: 'p',
        },
        headerName: 'Kind',
      },
    ],
    [],
  );

  useMemo(() => {
    const fields = columns.map(col => col.field || '');
    const filtered = systems?.filter((row: SystemItem) => {
      const rowItems = fields.map(field => getObjectPropFromString(row, field));
      return rowItems.find(
        item => item && item?.toLowerCase().includes(search.toLowerCase()),
      );
    });

    setTableRows(filtered!);
  }, [systems, search, columns]);

  const getRowId = (r: SystemItem) => r.id.toString();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Enter' && dockableCtx?.isMobile) {
        dockableCtx.setPrimaryCollapsed(true);
      }
    },
    [dockableCtx],
  );

  return (
    <Box
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      onKeyDown={handleKeyDown}
    >
      <div className={classes.content}>
        <WbResponsiveTableToolbar
          onClear={() => {
            setSearch('');
          }}
          searchProps={{
            value: search,
            onChange: (value: string) => {
              setPaginationOP({
                limit: paginationOP.limit,
                offset: paginationOP.offset,
              });
              setSearch(value);
            },
          }}
        />
        <WbTable<SystemItem>
          stickyHeader
          classNames={{ container: classes.table }}
          styles={{
            body: {
              borderBottom: `1px solid ${theme.palette.grey[200]}`,
            },
          }}
          selection="single"
          selectedItems={selectedRow ? [selectedRow] : []}
          setSelectedItems={items => {
            setSelectedRow(
              Array.isArray(items) && items.length ? items[0] : systems?.[0],
            );
          }}
          components={{
            tableContent: {
              getRowId,
              columns,
              rows: tableRows || [],
            },
          }}
        />
      </div>
      <WbPagination
        count={systems?.length ?? 0}
        offset={paginationOP.offset}
        limit={paginationOP.limit}
        onPageChange={newPage => {
          setPaginationOP({
            limit: paginationOP.limit,
            offset: newPage * paginationOP.limit,
          });
        }}
        onRowsPerPageChange={newRowsPerPage => {
          setPaginationOP({
            limit: newRowsPerPage,
            offset: 0,
          });
        }}
        style={{
          width: '100%',
          alignSelf: 'flex-end',
          backgroundColor: theme.palette.background.default,
          borderTop: `1px solid ${theme.palette.grey[200]}`,
        }}
      />
    </Box>
  );
};
